import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { User } from '../types';
import { userService } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error acquiring token silently:', error);
      try {
        // Use redirect instead of popup for token acquisition too
        await instance.acquireTokenRedirect(loginRequest);
        return null;
      } catch (redirectError) {
        console.error('Error acquiring token via redirect:', redirectError);
        return null;
      }
    }
  }, [account, instance]);

  const login = useCallback(async () => {
    try {
      // Use redirect flow instead of popup - more reliable with custom domains
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
    setUser(null);
  }, [instance]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      // Wait for MSAL to finish any interactions
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      // If not authenticated, stop loading and don't fetch
      if (!isAuthenticated || !account) {
        setIsLoading(false);
        setUser(null);
        return;
      }

      // User is authenticated, fetch their info
      setIsLoading(true);
      try {
        const token = await getAccessToken();
        if (token) {
          const response = await userService.getCurrentUser(token);
          if (response.success && response.data) {
            setUser(response.data);
          } else {
            console.error('Failed to get user info:', response.error);
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [account, inProgress, isAuthenticated, getAccessToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: isLoading || inProgress !== InteractionStatus.None,
    isAdmin: user?.isAdmin || false,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
